import React, { useState, useEffect, lazy, Suspense } from 'react';
import axios from 'axios';
import { getAuthHeader, useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
const DeadlineAlerts = lazy(() => import('../components/DeadlineAlerts'));
const ReferralKpiWidget = lazy(() => import('../components/ReferralKpiWidget'));
import {
  Users,
  Briefcase,
  FileText,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Activity,
  Lock,
  Bell,
  Calendar,
  Megaphone,
  Star,
  AlertTriangle,
  Clock,
  Package,
  ChevronLeft,
  ChevronRight,
  UserCheck,
  MessageSquare,
  Globe,
  ListTodo,
  Timer,
  UserPlus,
  Target,
  CheckCircle,
  AlertCircle,
  PackageX,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { AnalyticsDashboard } from '../components/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// الصلاحيات الافتراضية لكل دور - تُستخدم فقط إذا لم يتم تعيين صلاحيات مخصصة
const DEFAULT_PERMISSIONS = {
  admin: ['dashboard', 'clients', 'projects', 'invoices', 'expenses', 'employees', 'services', 'reports'],
  accountant: ['dashboard', 'clients', 'invoices', 'expenses', 'reports'],
  designer: ['dashboard', 'projects'],
  admin_employee: ['dashboard', 'clients', 'employees'],
  data_entry: ['dashboard', 'clients', 'projects']
};

export const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [currency, setCurrency] = useState('IQD');
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [recentReviews, setRecentReviews] = useState([]);
  const [portalStats, setPortalStats] = useState(null);
  const [taskStats, setTaskStats] = useState(null);
  const [timeStats, setTimeStats] = useState(null);
  const [leadStats, setLeadStats] = useState(null);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const { user } = useAuth();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  
  // التحقق من الصلاحيات - إذا كان لديه صلاحيات مخصصة استخدمها، وإلا استخدم الافتراضية
  const isAdmin = user?.role === 'admin' || user?.role === 'deputy_manager';
  
  // إذا كان لديه صلاحيات مخصصة (مصفوفة غير فارغة) استخدمها
  // إذا كانت الصلاحيات null أو undefined استخدم الافتراضية
  const hasCustomPermissions = Array.isArray(user?.permissions) && user.permissions.length > 0;
  const userPermissions = hasCustomPermissions ? user.permissions : (DEFAULT_PERMISSIONS[user?.role] || []);
  
  const hasDashboardPermission = isAdmin || userPermissions.includes('dashboard');
  
  // التحقق من صلاحية عرض البيانات المالية - بناءً على الصلاحيات فقط
  // المدير يرى كل شيء، باقي المستخدمين يرون البيانات المالية فقط إذا كانت لديهم صلاحية invoices أو expenses
  const canViewFinancials = isAdmin || userPermissions.includes('invoices') || userPermissions.includes('expenses');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchStats();
    fetchDashboardExtras();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats/dashboard`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardExtras = async () => {
    try {
      const [alertsRes, appointmentsRes, campaignsRes, reviewsRes, portalStatsRes, taskStatsRes, timeStatsRes, leadStatsRes, lowStockRes] = await Promise.all([
        axios.get(`${API}/notifications-system/alerts`, { headers: getAuthHeader() }).catch(() => ({ data: { alerts: [] } })),
        axios.get(`${API}/calendar/today`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API}/campaigns?status=active`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API}/reviews?status=approved`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API}/portal/admin/stats`, { headers: getAuthHeader() }).catch(() => ({ data: null })),
        axios.get(`${API}/tasks/stats`, { headers: getAuthHeader() }).catch(() => ({ data: null })),
        axios.get(`${API}/time-tracking/summary`, { headers: getAuthHeader() }).catch(() => ({ data: null })),
        axios.get(`${API}/leads/stats`, { headers: getAuthHeader() }).catch(() => ({ data: null })),
        axios.get(`${API}/inventory/low-stock`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);
      setAlerts((alertsRes.data.alerts || []).slice(0, 5));
      setTodayAppointments((appointmentsRes.data || []).slice(0, 5));
      setActiveCampaigns((campaignsRes.data || []).slice(0, 3));
      setRecentReviews((reviewsRes.data || []).slice(0, 3));
      setPortalStats(portalStatsRes.data);
      setTaskStats(taskStatsRes.data);
      setTimeStats(timeStatsRes.data);
      setLeadStats(leadStatsRes.data);
      setLowStockItems(lowStockRes.data || []);
    } catch (error) {
    }
  };

  const formatCurrency = (amount, curr = currency) => {
    const value = curr === 'IQD' ? amount : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value);
  };

  const StatCard = ({ title, value, icon: Icon, color, trend, dataTestId }) => (
    <Card className="hover:shadow-lg transition-shadow duration-200" data-testid={dataTestId}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium font-cairo text-slate-600">
          {title}
        </CardTitle>
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon size={20} className="text-white" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold font-inter">{value}</div>
        {trend && (
          <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
            {trend > 0 ? (
              <TrendingUp size={14} className="text-green-500" />
            ) : (
              <TrendingDown size={14} className="text-red-500" />
            )}
            <span>{t('thisMonth')}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Mobile Stat Card Component
  const MobileStatCard = ({ title, value, currency, icon, color }) => (
    <div className={`flex-shrink-0 w-36 p-4 rounded-xl bg-gradient-to-br ${color} text-white shadow-lg`}>
      <div className="flex items-center justify-between mb-2">
        <div className="p-2 bg-white/20 rounded-lg">
          {icon}
        </div>
      </div>
      <div className="text-lg font-bold font-cairo truncate">
        {value}
        {currency && <span className="text-xs mr-1">{currency}</span>}
      </div>
      <div className="text-xs text-white/80 font-cairo mt-1 truncate">{title}</div>
    </div>
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Activity className="animate-spin h-12 w-12 text-primary mx-auto mb-4" />
            <p className="text-slate-600 font-cairo">{t('loading')}</p>
          </div>
        </div>
      </Layout>
    );
  }

  // إذا لم يكن لديه صلاحية لوحة التحكم
  if (!hasDashboardPermission) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Lock className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold font-cairo text-slate-700 mb-2">{t('accessDenied')}</h2>
            <p className="text-slate-500 font-cairo">{t('noPermission')}</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4 lg:space-y-6" data-testid="dashboard-page">
        {/* Deadline Alerts - تنبيهات المواعيد */}
        {isAdmin && (
          <Suspense fallback={null}>
            <DeadlineAlerts />
          </Suspense>
        )}

        {/* Referral KPI widget (admin only — silently hides for others) */}
        {isAdmin && (
          <Suspense fallback={null}>
            <ReferralKpiWidget />
          </Suspense>
        )}

        {/* Header - Responsive */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h1 className="text-2xl lg:text-4xl font-bold font-cairo text-primary">{t('dashboard')}</h1>
          <div className="flex gap-2 flex-wrap">
            {/* زر لوحة الإحصائيات */}
            <Button
              onClick={() => setShowAnalytics(!showAnalytics)}
              variant={showAnalytics ? "default" : "outline"}
              className={`font-cairo ${showAnalytics ? 'bg-gradient-to-r from-blue-600 to-purple-600' : ''}`}
              data-testid="analytics-toggle-btn"
            >
              <BarChart3 size={18} className="ml-2" />
              الإحصائيات المتقدمة
            </Button>
            {canViewFinancials && (
              <>
                <button
                  onClick={() => setCurrency('IQD')}
                  className={`px-3 lg:px-4 py-1.5 lg:py-2 rounded-md font-cairo text-sm lg:text-base transition-all duration-200 ${
                    currency === 'IQD'
                      ? 'bg-primary text-white shadow-md'
                      : 'bg-white text-slate-700 hover:bg-slate-100'
                  }`}
                  data-testid="currency-iqd-btn"
                >
                  {t('iqd')}
                </button>
                <button
                  onClick={() => setCurrency('USD')}
                  className={`px-3 lg:px-4 py-1.5 lg:py-2 rounded-md font-cairo text-sm lg:text-base transition-all duration-200 ${
                    currency === 'USD'
                      ? 'bg-primary text-white shadow-md'
                      : 'bg-white text-slate-700 hover:bg-slate-100'
                  }`}
                  data-testid="currency-usd-btn"
                >
                  {t('usd')}
                </button>
              </>
            )}
          </div>
        </div>

        {/* لوحة الإحصائيات المتقدمة */}
        {showAnalytics && (
          <div className="bg-white rounded-2xl shadow-lg p-6 border">
            <AnalyticsDashboard />
          </div>
        )}

        {/* البيانات المالية - تظهر فقط للمدير والمحاسب */}
        {canViewFinancials && (
          <>
            {/* Mobile: Horizontal Scroll */}
            <div className="lg:hidden -mx-4 px-4 mb-6">
              <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2">
                <MobileStatCard
                  title={t('totalRevenue')}
                  value={formatCurrency(currency === 'IQD' ? stats?.total_revenue_iqd : stats?.total_revenue_usd)}
                  currency={currency}
                  icon={<DollarSign size={18} />}
                  color="from-green-500 to-green-600"
                />
                <MobileStatCard
                  title={t('totalExpenses')}
                  value={formatCurrency(currency === 'IQD' ? stats?.total_expenses_iqd : stats?.total_expenses_usd)}
                  currency={currency}
                  icon={<TrendingDown size={18} />}
                  color="from-red-500 to-red-600"
                />
                <MobileStatCard
                  title={t('netProfit')}
                  value={formatCurrency(currency === 'IQD' ? stats?.profit_iqd : stats?.profit_usd)}
                  currency={currency}
                  icon={<TrendingUp size={18} />}
                  color="from-blue-500 to-blue-600"
                />
                <MobileStatCard
                  title={t('clientsCount')}
                  value={stats?.total_clients || 0}
                  icon={<Users size={18} />}
                  color="from-purple-500 to-purple-600"
                />
              </div>
            </div>
            
            {/* Desktop: Grid */}
            <div className="hidden lg:grid grid-cols-5 gap-6">
              <StatCard
                title={t('totalRevenue')}
                value={`${formatCurrency(currency === 'IQD' ? stats?.total_revenue_iqd : stats?.total_revenue_usd)} ${currency}`}
                icon={DollarSign}
                color="bg-green-500"
                dataTestId="revenue-card"
              />
              <StatCard
                title={t('totalExpenses')}
                value={`${formatCurrency(currency === 'IQD' ? stats?.total_expenses_iqd : stats?.total_expenses_usd)} ${currency}`}
                icon={TrendingDown}
                color="bg-red-500"
                dataTestId="expenses-card"
              />
              <StatCard
                title={t('netProfit')}
                value={`${formatCurrency(currency === 'IQD' ? stats?.profit_iqd : stats?.profit_usd)} ${currency}`}
                icon={TrendingUp}
                color="bg-blue-500"
                dataTestId="profit-card"
              />
              <StatCard
                title={t('totalTax')}
                value={`${formatCurrency(currency === 'IQD' ? stats?.total_tax_iqd : stats?.total_tax_usd)} ${currency}`}
                icon={FileText}
                color="bg-amber-500"
                dataTestId="tax-card"
              />
              <StatCard
                title={t('clientsCount')}
                value={stats?.total_clients || 0}
                icon={Users}
                color="bg-purple-500"
                dataTestId="clients-card"
              />
            </div>
          </>
        )}

        {/* إحصائيات المشاريع - تظهر لجميع المستخدمين */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-6">
          <StatCard
            title={t('activeProjects')}
            value={stats?.active_projects || 0}
            icon={Briefcase}
            color="bg-accent"
            dataTestId="active-projects-card"
          />
          <StatCard
            title={t('totalProjects')}
            value={stats?.total_projects || 0}
            icon={Briefcase}
            color="bg-slate-500"
            dataTestId="total-projects-card"
          />
          {canViewFinancials && (
            <StatCard
              title={t('unpaidInvoices')}
              value={stats?.unpaid_invoices_count || 0}
              icon={FileText}
              color="bg-orange-500"
              dataTestId="unpaid-invoices-card"
            />
          )}
        </div>

        {/* البطاقات التفصيلية */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* الملخص المالي - فقط للمدير والمحاسب */}
          {canViewFinancials && (
            <Card data-testid="financial-summary-card">
              <CardHeader>
                <CardTitle className="font-cairo text-primary">{t('financialSummary')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
                    <span className="font-cairo text-slate-700">{t('totalRevenue')}</span>
                    <span className="font-bold font-inter text-green-600">
                      {formatCurrency(currency === 'IQD' ? stats?.total_revenue_iqd : stats?.total_revenue_usd)} {currency}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-red-50 rounded-lg">
                    <span className="font-cairo text-slate-700">{t('totalExpenses')}</span>
                    <span className="font-bold font-inter text-red-600">
                      {formatCurrency(currency === 'IQD' ? stats?.total_expenses_iqd : stats?.total_expenses_usd)} {currency}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-amber-50 rounded-lg">
                    <span className="font-cairo text-slate-700">{t('totalTax')}</span>
                    <span className="font-bold font-inter text-amber-600">
                      {formatCurrency(currency === 'IQD' ? stats?.total_tax_iqd : stats?.total_tax_usd)} {currency}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                    <span className="font-cairo font-semibold text-slate-700">{t('netProfit')}</span>
                    <span className="font-bold font-inter text-xl text-blue-600">
                      {formatCurrency(currency === 'IQD' ? stats?.profit_iqd : stats?.profit_usd)} {currency}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* نظرة عامة - تظهر لجميع المستخدمين */}
          <Card data-testid="overview-card">
            <CardHeader>
              <CardTitle className="font-cairo text-primary">{t('overview')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 border-b">
                  <div className="flex items-center gap-3">
                    <Users className="text-purple-500" size={20} />
                    <span className="font-cairo text-slate-700">{t('clientsCount')}</span>
                  </div>
                  <span className="font-bold font-inter">{stats?.total_clients || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 border-b">
                  <div className="flex items-center gap-3">
                    <Briefcase className="text-accent" size={20} />
                    <span className="font-cairo text-slate-700">{t('activeProjects')}</span>
                  </div>
                  <span className="font-bold font-inter">{stats?.active_projects || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 border-b">
                  <div className="flex items-center gap-3">
                    <Briefcase className="text-slate-500" size={20} />
                    <span className="font-cairo text-slate-700">{t('totalProjects')}</span>
                  </div>
                  <span className="font-bold font-inter">{stats?.total_projects || 0}</span>
                </div>
                {canViewFinancials && (
                  <div className="flex items-center justify-between p-3">
                    <div className="flex items-center gap-3">
                      <FileText className="text-orange-500" size={20} />
                      <span className="font-cairo text-slate-700">{t('unpaidInvoices')}</span>
                    </div>
                    <span className="font-bold font-inter text-orange-600">{stats?.unpaid_invoices_count || 0}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* قسم الملخص الشامل - الإشعارات والمواعيد والحملات */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {/* ملخص إدارة المشاريع - المهام، تتبع الوقت، العملاء المحتملين */}
          {(taskStats || timeStats || leadStats) && (
            <Card data-testid="project-management-summary" className="lg:col-span-3 bg-gradient-to-br from-slate-50 to-blue-50 border-2 border-blue-200">
              <CardHeader className="pb-3">
                <CardTitle className="font-cairo text-slate-800 flex items-center gap-2">
                  <Target className="h-5 w-5 text-blue-600" />
                  {t('projectManagement') || 'إدارة المشاريع'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  
                  {/* إحصائيات المهام */}
                  <div 
                    className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 cursor-pointer hover:shadow-md hover:border-indigo-300 transition-all"
                    onClick={() => navigate('/tasks')}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-indigo-100 rounded-lg">
                          <ListTodo className="h-5 w-5 text-indigo-600" />
                        </div>
                        <span className="font-semibold font-cairo text-slate-700">{t('tasks') || 'المهام'}</span>
                      </div>
                      <ChevronLeft className="h-4 w-4 text-slate-400" />
                    </div>
                    {taskStats ? (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div className="text-center p-2 bg-slate-50 rounded-lg">
                            <div className="text-xl font-bold text-indigo-600">{taskStats.total || 0}</div>
                            <div className="text-xs text-slate-500 font-cairo">{t('total') || 'الإجمالي'}</div>
                          </div>
                          <div className="text-center p-2 bg-green-50 rounded-lg">
                            <div className="text-xl font-bold text-green-600">{taskStats.by_status?.completed || 0}</div>
                            <div className="text-xs text-slate-500 font-cairo">{t('completed') || 'مكتملة'}</div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="text-center p-2 bg-blue-50 rounded-lg">
                            <div className="text-lg font-bold text-blue-600">{taskStats.by_status?.in_progress || 0}</div>
                            <div className="text-xs text-slate-500 font-cairo">{t('inProgress') || 'قيد التنفيذ'}</div>
                          </div>
                          <div className="text-center p-2 bg-red-50 rounded-lg">
                            <div className="text-lg font-bold text-red-600">{taskStats.overdue || 0}</div>
                            <div className="text-xs text-slate-500 font-cairo">{t('overdue') || 'متأخرة'}</div>
                          </div>
                        </div>
                        {/* شريط التقدم */}
                        {taskStats.total > 0 && (
                          <div className="mt-2">
                            <div className="flex justify-between text-xs text-slate-500 mb-1">
                              <span className="font-cairo">{t('completionRate') || 'نسبة الإنجاز'}</span>
                              <span>{Math.round(((taskStats.by_status?.completed || 0) / taskStats.total) * 100)}%</span>
                            </div>
                            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full transition-all duration-500"
                                style={{ width: `${((taskStats.by_status?.completed || 0) / taskStats.total) * 100}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-slate-400 text-sm font-cairo">{t('noData') || 'لا توجد بيانات'}</div>
                    )}
                  </div>

                  {/* إحصائيات تتبع الوقت */}
                  <div 
                    className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 cursor-pointer hover:shadow-md hover:border-emerald-300 transition-all"
                    onClick={() => navigate('/time-tracking')}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-emerald-100 rounded-lg">
                          <Timer className="h-5 w-5 text-emerald-600" />
                        </div>
                        <span className="font-semibold font-cairo text-slate-700">{t('timeTracking') || 'تتبع الوقت'}</span>
                      </div>
                      <ChevronLeft className="h-4 w-4 text-slate-400" />
                    </div>
                    {timeStats ? (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div className="text-center p-2 bg-emerald-50 rounded-lg">
                            <div className="text-xl font-bold text-emerald-600">{timeStats.total_hours?.toFixed(1) || 0}</div>
                            <div className="text-xs text-slate-500 font-cairo">{t('totalHours') || 'إجمالي الساعات'}</div>
                          </div>
                          <div className="text-center p-2 bg-green-50 rounded-lg">
                            <div className="text-xl font-bold text-green-600">{timeStats.billable_hours?.toFixed(1) || 0}</div>
                            <div className="text-xs text-slate-500 font-cairo">{t('billable') || 'قابلة للفوترة'}</div>
                          </div>
                        </div>
                        <div className="text-center p-2 bg-orange-50 rounded-lg">
                          <div className="text-lg font-bold text-orange-600">{timeStats.non_billable_hours?.toFixed(1) || 0}</div>
                          <div className="text-xs text-slate-500 font-cairo">{t('nonBillable') || 'غير قابلة للفوترة'}</div>
                        </div>
                        {/* أعلى المشاريع */}
                        {timeStats.by_project && timeStats.by_project.length > 0 && (
                          <div className="mt-2 pt-2 border-t">
                            <div className="text-xs text-slate-500 font-cairo mb-1">{t('topProject') || 'أعلى مشروع'}</div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-slate-700 truncate max-w-[120px]">
                                {timeStats.by_project[0]?.project_name || '-'}
                              </span>
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                {timeStats.by_project[0]?.total_hours?.toFixed(1)} h
                              </Badge>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-slate-400 text-sm font-cairo">{t('noData') || 'لا توجد بيانات'}</div>
                    )}
                  </div>

                  {/* إحصائيات العملاء المحتملين */}
                  <div 
                    className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 cursor-pointer hover:shadow-md hover:border-purple-300 transition-all"
                    onClick={() => navigate('/leads')}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <UserPlus className="h-5 w-5 text-purple-600" />
                        </div>
                        <span className="font-semibold font-cairo text-slate-700">{t('leads') || 'العملاء المحتملين'}</span>
                      </div>
                      <ChevronLeft className="h-4 w-4 text-slate-400" />
                    </div>
                    {leadStats ? (
                      <div className="space-y-2">
                        <div className="grid grid-cols-3 gap-2">
                          <div className="text-center p-2 bg-purple-50 rounded-lg">
                            <div className="text-lg font-bold text-purple-600">{leadStats.total || 0}</div>
                            <div className="text-xs text-slate-500 font-cairo">{t('total') || 'الكل'}</div>
                          </div>
                          <div className="text-center p-2 bg-blue-50 rounded-lg">
                            <div className="text-lg font-bold text-blue-600">{leadStats.by_stage?.new || 0}</div>
                            <div className="text-xs text-slate-500 font-cairo">{t('new') || 'جديد'}</div>
                          </div>
                          <div className="text-center p-2 bg-green-50 rounded-lg">
                            <div className="text-lg font-bold text-green-600">{leadStats.by_stage?.won || 0}</div>
                            <div className="text-xs text-slate-500 font-cairo">{t('won') || 'مكتسب'}</div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="text-center p-2 bg-amber-50 rounded-lg">
                            <div className="text-sm font-bold text-amber-600">${(leadStats.pipeline_value || 0).toLocaleString()}</div>
                            <div className="text-xs text-slate-500 font-cairo">{t('pipelineValue') || 'قيمة Pipeline'}</div>
                          </div>
                          <div className="text-center p-2 bg-teal-50 rounded-lg">
                            <div className="text-sm font-bold text-teal-600">{leadStats.conversion_rate || 0}%</div>
                            <div className="text-xs text-slate-500 font-cairo">{t('conversionRate') || 'نسبة التحويل'}</div>
                          </div>
                        </div>
                        {/* Pipeline مصغر */}
                        <div className="flex gap-1 mt-2 pt-2 border-t">
                          {['new', 'contacted', 'qualified', 'won'].map((stage, idx) => (
                            <div 
                              key={stage}
                              className={`flex-1 h-2 rounded-full ${
                                stage === 'new' ? 'bg-blue-400' :
                                stage === 'contacted' ? 'bg-cyan-400' :
                                stage === 'qualified' ? 'bg-yellow-400' :
                                'bg-green-400'
                              }`}
                              style={{ 
                                opacity: leadStats.by_stage?.[stage] > 0 ? 1 : 0.3
                              }}
                              title={`${stage}: ${leadStats.by_stage?.[stage] || 0}`}
                            />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4 text-slate-400 text-sm font-cairo">{t('noData') || 'لا توجد بيانات'}</div>
                    )}
                  </div>
                  
                </div>
              </CardContent>
            </Card>
          )}

          {/* التنبيهات العاجلة */}
          <Card data-testid="urgent-alerts-card" className={`border-4 ${isRTL ? 'border-r-red-500 border-l-0 border-t-0 border-b-0' : 'border-l-red-500 border-r-0 border-t-0 border-b-0'}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-cairo text-slate-800 flex items-center gap-2">
                  <Bell className="h-5 w-5 text-red-500" />
                  {t('urgentNotifications')}
                </CardTitle>
                <Badge variant="destructive">{alerts.length}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {alerts.length === 0 ? (
                <div className="text-center py-4 text-slate-500">
                  <Bell className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">{t('noData')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {alerts.map((alert, idx) => (
                    <div key={alert.id || `alert-${idx}`} className="flex items-start gap-2 p-2 bg-slate-50 rounded-lg">
                      <div className={`p-1 rounded ${
                        alert.priority === 'urgent' ? 'bg-red-100' :
                        alert.priority === 'high' ? 'bg-orange-100' : 'bg-blue-100'
                      }`}>
                        {alert.type === 'low_inventory' ? <Package className="h-4 w-4 text-orange-600" /> :
                         alert.type === 'payment_overdue' ? <AlertTriangle className="h-4 w-4 text-red-600" /> :
                         <FileText className="h-4 w-4 text-blue-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{alert.title}</p>
                        <p className="text-xs text-slate-500 truncate">{alert.message}</p>
                      </div>
                    </div>
                  ))}
                  <button 
                    onClick={() => navigate('/notifications-system')}
                    className="w-full text-center text-sm text-primary hover:underline mt-2 flex items-center justify-center gap-1"
                  >
                    {t('view')}
                    {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* تنبيهات المخزون المنخفض */}
          {lowStockItems.length > 0 && (
            <Card data-testid="low-stock-alerts-card" className={`border-4 ${isRTL ? 'border-r-orange-500 border-l-0 border-t-0 border-b-0' : 'border-l-orange-500 border-r-0 border-t-0 border-b-0'}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-cairo text-slate-800 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-orange-500" />
                    {t('lowStockAlerts') || 'تنبيهات المخزون'}
                  </CardTitle>
                  <Badge className="bg-orange-500">{lowStockItems.length}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {lowStockItems.slice(0, 5).map((item) => (
                    <div 
                      key={item.id || item.name} 
                      className={`flex items-center justify-between p-2 rounded-lg ${
                        item.status === 'critical' ? 'bg-red-50 border border-red-200' :
                        item.status === 'warning' ? 'bg-orange-50 border border-orange-200' :
                        'bg-yellow-50 border border-yellow-200'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded ${
                          item.status === 'critical' ? 'bg-red-100' :
                          item.status === 'warning' ? 'bg-orange-100' : 'bg-yellow-100'
                        }`}>
                          {item.status === 'critical' ? (
                            <PackageX className="h-4 w-4 text-red-600" />
                          ) : (
                            <Package className="h-4 w-4 text-orange-600" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">{item.name}</p>
                          <p className="text-xs text-slate-500">
                            {item.status === 'critical' ? 'نفذ من المخزون!' : `الكمية: ${item.quantity} ${item.unit}`}
                          </p>
                        </div>
                      </div>
                      <div className="text-left">
                        <Badge className={
                          item.status === 'critical' ? 'bg-red-500' :
                          item.status === 'warning' ? 'bg-orange-500' : 'bg-yellow-500'
                        }>
                          {item.status === 'critical' ? 'نفذ' : item.status === 'warning' ? 'منخفض جداً' : 'منخفض'}
                        </Badge>
                        <p className="text-xs text-slate-400 mt-1">الحد الأدنى: {item.min_quantity}</p>
                      </div>
                    </div>
                  ))}
                  {lowStockItems.length > 5 && (
                    <p className="text-center text-xs text-slate-500">
                      و {lowStockItems.length - 5} عناصر أخرى
                    </p>
                  )}
                  <button 
                    onClick={() => navigate('/inventory')}
                    className="w-full text-center text-sm text-orange-600 hover:underline mt-2 flex items-center justify-center gap-1"
                  >
                    عرض المخزون
                    {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* مواعيد اليوم */}
          <Card data-testid="today-appointments-card" className={`border-4 ${isRTL ? 'border-r-blue-500 border-l-0 border-t-0 border-b-0' : 'border-l-blue-500 border-r-0 border-t-0 border-b-0'}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-cairo text-slate-800 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-500" />
                  {t('todayAppointments')}
                </CardTitle>
                <Badge className="bg-blue-500">{todayAppointments.length}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {todayAppointments.length === 0 ? (
                <div className="text-center py-4 text-slate-500">
                  <Calendar className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">{t('noData')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {todayAppointments.map((apt) => (
                    <div key={apt.id || apt.time} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                      <div className={`w-2 h-8 rounded`} style={{ backgroundColor: apt.color || '#3b82f6' }}></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{apt.title}</p>
                        <p className="text-xs text-slate-500 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(apt.start_date).toLocaleTimeString('ar-IQ', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))}
                  <button 
                    onClick={() => navigate('/calendar')}
                    className="w-full text-center text-sm text-primary hover:underline mt-2 flex items-center justify-center gap-1"
                  >
                    {t('calendar')}
                    {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* الحملات النشطة */}
          <Card data-testid="active-campaigns-card" className={`border-4 ${isRTL ? 'border-r-green-500 border-l-0 border-t-0 border-b-0' : 'border-l-green-500 border-r-0 border-t-0 border-b-0'}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-cairo text-slate-800 flex items-center gap-2">
                  <Megaphone className="h-5 w-5 text-green-500" />
                  {t('activeCampaigns')}
                </CardTitle>
                <Badge className="bg-green-500">{activeCampaigns.length}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {activeCampaigns.length === 0 ? (
                <div className="text-center py-4 text-slate-500">
                  <Megaphone className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">{t('noData')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {activeCampaigns.map((campaign) => (
                    <div key={campaign.id || campaign.name} className="p-2 bg-slate-50 rounded-lg">
                      <p className="text-sm font-medium text-slate-800 truncate">{campaign.name}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-slate-500">{campaign.client_name}</span>
                        <span className="text-xs font-medium text-green-600">
                          {campaign.budget_iqd?.toLocaleString()} IQD
                        </span>
                      </div>
                    </div>
                  ))}
                  <button 
                    onClick={() => navigate('/campaigns')}
                    className="w-full text-center text-sm text-primary hover:underline mt-2 flex items-center justify-center gap-1"
                  >
                    {t('view')}
                    {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* بوابة العملاء */}
        {isAdmin && portalStats && (
          <Card data-testid="portal-stats-card" className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-cairo text-slate-800 flex items-center gap-2">
                  <Globe className="h-5 w-5 text-indigo-500" />
                  {t('customerPortal') || 'بوابة العملاء'}
                </CardTitle>
                <button 
                  onClick={() => navigate('/clients')}
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                >
                  {t('manage') || 'إدارة'}
                  {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                {/* حسابات البوابة */}
                <div className="bg-white/80 rounded-xl p-4 text-center shadow-sm">
                  <div className="flex items-center justify-center mb-2">
                    <div className="p-2 bg-indigo-100 rounded-full">
                      <UserCheck className="h-5 w-5 text-indigo-600" />
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-indigo-600">{portalStats.accounts?.total || 0}</div>
                  <div className="text-xs text-slate-600 font-cairo">{t('totalAccounts') || 'إجمالي الحسابات'}</div>
                </div>
                
                {/* حسابات نشطة */}
                <div className="bg-white/80 rounded-xl p-4 text-center shadow-sm">
                  <div className="flex items-center justify-center mb-2">
                    <div className="p-2 bg-green-100 rounded-full">
                      <Activity className="h-5 w-5 text-green-600" />
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-green-600">{portalStats.accounts?.active || 0}</div>
                  <div className="text-xs text-slate-600 font-cairo">{t('activeAccounts') || 'حسابات نشطة'}</div>
                </div>
                
                {/* التذاكر المفتوحة */}
                <div className="bg-white/80 rounded-xl p-4 text-center shadow-sm">
                  <div className="flex items-center justify-center mb-2">
                    <div className="p-2 bg-orange-100 rounded-full">
                      <MessageSquare className="h-5 w-5 text-orange-600" />
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-orange-600">{portalStats.tickets?.open || 0}</div>
                  <div className="text-xs text-slate-600 font-cairo">{t('openTickets') || 'تذاكر مفتوحة'}</div>
                </div>
                
                {/* التذاكر العاجلة */}
                <div className="bg-white/80 rounded-xl p-4 text-center shadow-sm">
                  <div className="flex items-center justify-center mb-2">
                    <div className="p-2 bg-red-100 rounded-full">
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-red-600">{portalStats.tickets?.urgent || 0}</div>
                  <div className="text-xs text-slate-600 font-cairo">{t('urgentTickets') || 'تذاكر عاجلة'}</div>
                </div>
              </div>
              
              {/* آخر التذاكر */}
              {portalStats.recent_tickets && portalStats.recent_tickets.length > 0 && (
                <div className="bg-white/60 rounded-lg p-3">
                  <h4 className="text-sm font-semibold text-slate-700 mb-2 font-cairo">{t('recentTickets') || 'آخر التذاكر'}</h4>
                  <div className="space-y-2">
                    {portalStats.recent_tickets.slice(0, 3).map((ticket) => (
                      <div key={ticket.id || ticket.ticket_number} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                            ticket.priority === 'urgent' ? 'bg-red-500' :
                            ticket.priority === 'high' ? 'bg-orange-500' :
                            'bg-blue-500'
                          }`}></span>
                          <span className="text-sm text-slate-700 truncate">{ticket.subject}</span>
                        </div>
                        <Badge 
                          variant={ticket.status === 'open' ? 'destructive' : 'secondary'}
                          className="text-xs flex-shrink-0"
                        >
                          {ticket.status === 'open' ? (t('open') || 'مفتوحة') : 
                           ticket.status === 'in_progress' ? (t('inProgress') || 'قيد المعالجة') : 
                           (t('resolved') || 'تم الحل')}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* آخر التقييمات */}
        {recentReviews.length > 0 && (
          <Card data-testid="recent-reviews-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-cairo text-slate-800 flex items-center gap-2">
                  <Star className="h-5 w-5 text-yellow-500" />
                  {t('reviews')}
                </CardTitle>
                <button 
                  onClick={() => navigate('/reviews')}
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                >
                  {t('view')}
                  {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
                {recentReviews.map((review) => (
                  <div key={review.id || review.client_name} className="p-4 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg border border-yellow-200">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map(i => (
                          <Star 
                            key={`review-star-${i}`} 
                            className={`h-4 w-4 ${i <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} 
                          />
                        ))}
                      </div>
                      <span className="text-sm font-semibold">{review.rating}/5</span>
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2 mb-2">{review.review_text || t('noData')}</p>
                    <p className="text-xs text-slate-500">- {review.client_name}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};